import axios from "axios";

/**
 * Guest cart identity, mirrored in localStorage.
 *
 * The API issues the guest cart id as a `SameSite=None` cookie, but the
 * storefront and the API are different hosts, so Safari and Chrome's
 * third-party cookie rules drop it. Every add-to-cart then minted a fresh cart
 * and the basket looked like it emptied itself on reload. Keeping the id here
 * and sending it back on `X-Guest-Cart-Id` makes the basket survive without
 * depending on cross-site cookies.
 */

export const GUEST_CART_STORAGE_KEY = "cheepsweepz_guest_cart_id";
export const GUEST_CART_HEADER = "X-Guest-Cart-Id";

const GUEST_ID_PATTERN = /^guest_[A-Za-z0-9]+$/;

export function isGuestCartId(value) {
  return typeof value === "string" && GUEST_ID_PATTERN.test(value);
}

export function readGuestCartId() {
  if (typeof window === "undefined") return "";
  try {
    const stored = window.localStorage.getItem(GUEST_CART_STORAGE_KEY) || "";
    return isGuestCartId(stored) ? stored : "";
  } catch {
    return "";
  }
}

export function storeGuestCartId(value) {
  if (typeof window === "undefined" || !isGuestCartId(value)) return false;
  try {
    window.localStorage.setItem(GUEST_CART_STORAGE_KEY, value);
    return true;
  } catch {
    return false;
  }
}

export function clearGuestCartId() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(GUEST_CART_STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

/** Requests that operate on a basket and therefore need the guest identity. */
export function needsGuestCartHeader(url) {
  if (typeof url !== "string") return false;
  return /\/api\/cart(\/|$|\?)/.test(url);
}

/**
 * Apply what the server said about cart identity: remember a new guest id, and
 * drop the stored one once the basket has been merged into an account.
 */
export function syncGuestCartIdFromResponse(data) {
  if (!data || typeof data !== "object") return;
  if (data.guest_cart_merged) {
    clearGuestCartId();
    return;
  }
  if (isGuestCartId(data.guest_cart_id)) {
    storeGuestCartId(data.guest_cart_id);
  }
}

export function installGuestCartInterceptors() {
  axios.interceptors.request.use((config) => {
    const cartId = readGuestCartId();
    if (cartId && needsGuestCartHeader(config.url) && !config.headers?.[GUEST_CART_HEADER]) {
      config.headers = { ...config.headers, [GUEST_CART_HEADER]: cartId };
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      if (needsGuestCartHeader(response.config?.url)) {
        syncGuestCartIdFromResponse(response.data);
      }
      return response;
    },
    (error) => Promise.reject(error)
  );
}
